import type { BrokerAggregateStats, BrokerMetricsObjects, BrokerStat, Metric, SortColumn, SortDirection } from '@/types/main';
import { compareAsc, format, parse } from 'date-fns';
import {
    SECOND_MULTIPLIER,
    SORT_ASC,
    SORT_COLUMN_BROKER,
    SORT_COLUMN_MEDIAN,
    SORT_COLUMN_P10,
    SORT_COLUMN_P90,
    SORT_COLUMN_P99,
    SORT_COLUMN_STD_DEV,
} from '@/constants/broker_metrics';

function toMs(value: number | null): number | null {
    return value === null ? null : value * SECOND_MULTIPLIER;
}

function fastestOf(values: (number | null)[]): number | undefined {
    const defined = values.filter((value): value is number => value !== null);
    return defined.length ? Math.min(...defined) : undefined;
}

function vsFastest(value: number | null, fastestValue: number | undefined): number | null {
    if (value === null || fastestValue === undefined) {
        return null;
    }
    const delta = value - fastestValue;
    return delta === 0 ? null : delta;
}


// Each broker's median/p90/p99/p10/std dev is already aggregated server-side
// (see the /buckets response's per-broker `stats`) - this just converts to
// ms and computes the per-column "vs fastest" delta across the currently
// compared brokers, which is inherently a client-side concern since it
// depends on who else is in the comparison.
export function computeBrokerStats(data: Record<string, { stats: BrokerAggregateStats; color: string; displayName: string }>): BrokerStat[] {
    const raw = Object.entries(data).map(([broker, { stats, color, displayName }]) => ({
        broker,
        displayName,
        color,
        medianMs: toMs(stats.median),
        p90Ms: toMs(stats.percentile_90),
        p99Ms: toMs(stats.percentile_99),
        p10Ms: toMs(stats.percentile_10),
        stdDevMs: toMs(stats.standard_deviation),
    }));

    const fastestMedian = fastestOf(raw.map((row) => row.medianMs));
    const fastestP90 = fastestOf(raw.map((row) => row.p90Ms));
    const fastestP99 = fastestOf(raw.map((row) => row.p99Ms));
    const fastestP10 = fastestOf(raw.map((row) => row.p10Ms));
    const fastestStdDev = fastestOf(raw.map((row) => row.stdDevMs));

    return raw.map((row) => ({
        broker: row.broker,
        displayName: row.displayName,
        color: row.color,
        median: {
            valueMs: row.medianMs,
            vsFastestMs: vsFastest(row.medianMs, fastestMedian),
        },
        p90: {
            valueMs: row.p90Ms,
            vsFastestMs: vsFastest(row.p90Ms, fastestP90),
        },
        p99: {
            valueMs: row.p99Ms,
            vsFastestMs: vsFastest(row.p99Ms, fastestP99),
        },
        p10: {
            valueMs: row.p10Ms,
            vsFastestMs: vsFastest(row.p10Ms, fastestP10),
        },
        stdDev: {
            valueMs: row.stdDevMs,
            vsFastestMs: vsFastest(row.stdDevMs, fastestStdDev),
        },
    }));
}

// Broker-name sort in the given direction; the metric columns sort by their
// raw value with brokers that have no data for the active endpoint always
// pushed to the end, regardless of direction.
export function sortBrokerStats(rows: BrokerStat[], column: SortColumn, direction: SortDirection): BrokerStat[] {
    const sign = direction === SORT_ASC ? 1 : -1;
    const valueOf = (row: BrokerStat): number | string | null => {
        switch (column) {
            case SORT_COLUMN_BROKER:
                return row.displayName;
            case SORT_COLUMN_MEDIAN:
                return row.median.valueMs;
            case SORT_COLUMN_P90:
                return row.p90.valueMs;
            case SORT_COLUMN_P99:
                return row.p99.valueMs;
            case SORT_COLUMN_P10:
                return row.p10.valueMs;
            case SORT_COLUMN_STD_DEV:
                return row.stdDev.valueMs;
        }
    };

    return [...rows].sort((a, b) => {
        const valueA = valueOf(a);
        const valueB = valueOf(b);
        if (valueA === null && valueB === null) {
            return 0;
        }
        if (valueA === null) {
            return 1;
        }
        if (valueB === null) {
            return -1;
        }
        if (typeof valueA === 'string' || typeof valueB === 'string') {
            return sign * String(valueA).localeCompare(String(valueB));
        }
        return sign * (valueA - valueB);
    });
}



// The end-of-day cutoff for today's data, in IST. Data is only considered
// "for" a date once IST market hours have closed, hence anchoring to 23:59
// UTC rather than local midnight.
export function computeDayWindowEnd(): Date {
    const endDate = new Date();
    endDate.setUTCHours(23, 59);
    return endDate;
}

// The current calendar month as `yyyy-MM` - the chart's default selection.
export function currentMonthKey(): string {
    return format(new Date(), 'yyyy-MM');
}

// The `yyyy-MM-dd` first/last-day span of a `yyyy-MM` month, ready to hand
// to the buckets fetch as start/end. The end is clamped to today so the
// current month doesn't ask the API for dates that haven't happened yet.
export function monthRange(monthKey: string): { start: string; end: string } {
    const [year, month] = monthKey.split('-').map(Number);
    const lastDay = new Date(year, month, 0);
    const today = new Date();
    return {
        start: format(new Date(year, month - 1, 1), 'yyyy-MM-dd'),
        end: format(lastDay < today ? lastDay : today, 'yyyy-MM-dd'),
    };
}

// Inclusive list of `yyyy-MM` keys from `fromKey` to `toKey`, oldest first.
export function monthKeysBetween(fromKey: string, toKey: string): string[] {
    const [fromYear, fromMonth] = fromKey.split('-').map(Number);
    const [toYear, toMonth] = toKey.split('-').map(Number);
    const keys: string[] = [];
    for (let year = fromYear, month = fromMonth; year < toYear || (year === toYear && month <= toMonth); ) {
        keys.push(`${year}-${String(month).padStart(2, '0')}`);
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return keys;
}

// Every `yyyy-MM-dd` date within a `yyyy-MM` month.
export function daysInMonth(monthKey: string): string[] {
    const [year, month] = monthKey.split('-').map(Number);
    const count = new Date(year, month, 0).getDate();
    return Array.from({ length: count }, (_, i) => `${monthKey}-${String(i + 1).padStart(2, '0')}`);
}

// Only the brokers the caller actually knows about, in the order given -
// used to sanitize a `?brokers=` query param against the real broker list.
export function parseBrokersParam(param: string | null, knownBrokers: string[]): string[] {
    if (!param) {
        return [];
    }
    const requested = param.split(',').map((broker) => broker.trim());
    return requested.filter((broker) => knownBrokers.includes(broker));
}

// The shareable query string for the current comparison - omits `criteria`
// when it's just the default endpoint, and omits everything once there's
// nothing selected yet (e.g. still loading), so the URL never gets clobbered
// with a blank param mid-load.
export function buildCompareQueryString(comparedBrokers: string[], selectedEndpoint: string, defaultEndpoint: string): string {
    if (comparedBrokers.length === 0) {
        return '';
    }
    const params = new URLSearchParams();
    params.set('brokers', comparedBrokers.join(','));
    if (selectedEndpoint !== defaultEndpoint) {
        params.set('criteria', selectedEndpoint);
    }
    return `?${params.toString()}`;
}

export function convertToMap(metrics: Metric[], initialObject: Record<string, Metric>) {
    return metrics.reduce((prev: Record<string, Metric>, cur) => {
        prev[cur.end_time] = cur;
        return prev;
    }, initialObject);
}



export function parseAndFormatTime(dateString: string, parsingFor: string, parsingTo: string): string {
    const r = parse(dateString, parsingFor, new Date());
    if (isNaN(r.getTime())) {
        throw `Improperly formed date ${dateString}`;
    }
    return format(r, parsingTo);
}

// Backend broker colors are always 6-digit hex, so the box fills can be
// the same hue at a lower alpha - overlapping brokers stay readable where
// an opaque fill would just hide whoever is drawn underneath.
function withAlpha(hexColor: string, alpha: number): string {
    const hex = hexColor.replace('#', '');
    if (hex.length !== 6) {
        return hexColor;
    }
    const [r, g, b] = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Each timestamp that has any samples is one unit on a linear axis. Brokers
// that have no sample at that timestamp do not reserve a slot, so empty time
// and missing brokers take no width. `text` keeps the original timestamp for
// tick labels and the hover tooltip; `x` is only the packed position.
const BUCKET_SPAN = 0.92;

function boxPitch(count: number): { width: number; step: number } {
    if (count <= 1) {
        return { width: BUCKET_SPAN, step: 0 };
    }
    const gapRatio = 0.1;
    const width = BUCKET_SPAN / (count + gapRatio * (count - 1));
    return { width, step: width * (1 + gapRatio) };
}

// One box per bucket, per broker. Every bucket already arrives with its
// percentiles computed server-side, so the boxes are drawn from those
// precomputed stats rather than from raw samples: P10/P90 whiskers around a
// P25-P75 body, median line inside, dashed mean.
//
// Plotly ignores `hovertemplate` on box traces. Stats stay on `customdata`
// for a tooltip that is shown only while the pointer is inside the box.
export function createChartBoxes(data: [string, BrokerMetricsObjects][]) {
    const times = new Set<string>();
    for (const [, brokerData] of data) {
        for (const key of Object.keys(brokerData.metrics)) {
            times.add(key);
        }
    }
    const orderedTimes = Array.from(times).sort(compareAsc);
    const timeIndex = new Map(orderedTimes.map((time, index) => [time, index]));
    const brokersAtTime = new Map<string, string[]>();
    for (const [broker, brokerData] of data) {
        for (const key of Object.keys(brokerData.metrics)) {
            const present = brokersAtTime.get(key);
            if (present) {
                present.push(broker);
            } else {
                brokersAtTime.set(key, [broker]);
            }
        }
    }

    return data.flatMap(([broker, brokerData]) => {
        const sortedKeys = Object.keys(brokerData.metrics).sort(compareAsc);
        const toMsValue = (value: number) => value * SECOND_MULTIPLIER;
        const traceBase = {
            name: brokerData.displayName,
            // The broker's stable code, for matching against highlightedBroker -
            // `name` above is the display label and shouldn't double as an
            // identifier.
            meta: broker,
            legendgroup: broker,
            type: 'box' as const,
            boxmean: true,
            boxpoints: false as const,
            whiskerwidth: 0.5,
            line: { color: brokerData.color, width: 1 },
            fillcolor: withAlpha(brokerData.color, 0.35),
            hoverinfo: 'skip' as const,
        };
        // Plotly's box `width` is a single number, not an array, so each
        // bucket is its own trace. That lets a lone broker fill the slot
        // and a shared timestamp split the same slot among whoever is present.
        if (sortedKeys.length === 0) {
            return [
                {
                    ...traceBase,
                    x: [] as number[],
                    text: [] as string[],
                    showlegend: true,
                },
            ];
        }
        return sortedKeys.map((key, pointIndex) => {
            const bucket = brokerData.metrics[key];
            const index = timeIndex.get(key) ?? 0;
            const present = brokersAtTime.get(key) ?? [broker];
            const slot = Math.max(0, present.indexOf(broker));
            const { width, step } = boxPitch(present.length);
            return {
                ...traceBase,
                x: [index + (slot - (present.length - 1) / 2) * step],
                width,
                text: [key],
                lowerfence: [toMsValue(bucket.percentile_10)],
                q1: [toMsValue(bucket.percentile_25)],
                median: [toMsValue(bucket.median)],
                q3: [toMsValue(bucket.percentile_75)],
                upperfence: [toMsValue(bucket.percentile_90)],
                mean: [toMsValue(bucket.mean)],
                customdata: [
                    [
                        toMsValue(bucket.median),
                        toMsValue(bucket.mean),
                        toMsValue(bucket.percentile_25),
                        toMsValue(bucket.percentile_75),
                        toMsValue(bucket.percentile_10),
                        toMsValue(bucket.percentile_90),
                        toMsValue(bucket.percentile_99),
                        toMsValue(bucket.standard_deviation),
                        key,
                    ],
                ],
                showlegend: pointIndex === 0,
            };
        });
    });
}
